import { useStytchB2BClient } from '@stytch/react/b2b'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
	InviteStytchMember,
	StytchWorkspaceRole,
} from 'honeygrid-types/validators-and-types/StytchWorkspaceTypes'

export function useStytchWorkspaceMembers() {
	const client = useStytchB2BClient()
	const queryClient = useQueryClient()

	// List members via API
	const membersQuery = useQuery({
		queryKey: ['stytch-workspace-members'],
		queryFn: async () => client.organization.members.search({}),
	})

	// Invite member using Stytch B2B client
	const inviteMemberMutation = useMutation({
		mutationFn: async (params: InviteStytchMember) => {
			return await client.magicLinks.email.invite({
				email_address: params.email_address,
				name: params.name,
				roles: params.roles,
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['stytch-workspace-members'],
			})
		},
	})

	// Update member role
	const updateRoleMutation = useMutation({
		mutationFn: async ({
			member_id,
			roles,
		}: {
			member_id: string
			roles: StytchWorkspaceRole[]
		}) => {
			await client.organization.members.update({
				member_id,
				roles,
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['stytch-workspace-members'],
			})
		},
	})

	// Remove member via API
	const removeMemberMutation = useMutation({
		mutationFn: async (member_id: string) => {
			await client.organization.members.delete(member_id)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['stytch-workspace-members'],
			})
		},
	})

	return {
		members: membersQuery.data?.members || [],
		isLoading: membersQuery.isLoading,
		inviteMember: inviteMemberMutation.mutateAsync,
		updateRole: updateRoleMutation.mutateAsync,
		removeMember: removeMemberMutation.mutateAsync,
	}
}
